import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import _ from 'lodash';
import type { TicketingInstructionResponseArgs } from '@this/src/domain/arrangement/ticketing_instruction';
import TicketingInstruction, {
  convertTicketingInstructionResponseToArgs
} from '@this/src/domain/arrangement/ticketing_instruction';
import { Box } from '@material-ui/core';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from '@this/components/shared/ui/data_displays/table';
import type Supplier from '@this/src/domain/supplier/supplier';
import type SuppliedItem from '@this/src/domain/supplied_item/supplied_item';
import { ArrangementAPI as api } from '@this/src/api';
import type PaymentMethodList from '@this/domain/payment_method/payment_method_list';
import { Fetcher, HTTPError } from '@this/src/util';
import type IndividualRailwaySuppliedItem from '@this/domain/individual_railway_supplied_item/individual_railway_supplied_item';
import type { TabType } from '../todo_list';
import TicketingInstructionRow from './ticketing_instruction_row';
import TicketingInstructionSearchArea from './ticketing_instruction_search_area';

interface Response {
  ticketing_instructions: TicketingInstructionResponseArgs[];
  total_count: number;
  total_page: number;
}

interface Options {
  suppliers: Supplier[];
  suppliedItems: SuppliedItem[];
  paymentMethods: PaymentMethodList | null;
  individualRailwaySuppliedItems: IndividualRailwaySuppliedItem[];
  optionReady: boolean;
}

const tab: TabType = 'TICKETING_INSTRUCTION';

const initialOptions: Options = {
  suppliers: [],
  suppliedItems: [],
  paymentMethods: null,
  individualRailwaySuppliedItems: [],
  optionReady: false
};

const TicketingInstructionView = observer(() => {
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [ticketingInstcutions, setTicketingInstcutions] = useState<TicketingInstruction[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPage, setTotalPage] = useState(1);
  const [page, setPage] = useState(1);

  const queryRef = useRef<URLSearchParams | null>(null);
  const lastValidQueryRef = useRef<URLSearchParams | null>(null);

  const [options, setOptions] = useState<Options>(initialOptions);
  const { suppliers, suppliedItems, paymentMethods, individualRailwaySuppliedItems, optionReady } = options;

  const fetchOptions = useCallback(async () => {
    const res = await api.supplierOptions.list();
    setOptions({ ...res, optionReady: true });
  }, []);

  const fetchItems = useCallback(() => {
    if (queryRef.current === null) {
      console.error('queryRef.current is null');
      return;
    }

    setIsLoading(true);

    const params = Object.fromEntries((queryRef.current as any).entries());

    if (queryRef.current.has('tab')) {
      queryRef.current.delete('tab');
    }
    const query = queryRef.current.toString();
    history.push(`/arrangement/todo_list?tab=${tab}&${query}`);

    Fetcher.get<Response>('/arrangement/ticketing_instructions.json', params)
      .then(res => {
        setTicketingInstcutions(
          res.ticketing_instructions.map(
            args => new TicketingInstruction(convertTicketingInstructionResponseToArgs(args))
          )
        );
        setTotalCount(res.total_count);
        setTotalPage(res.total_page);
        if (res.total_page < page) {
          setPage(1);
        }

        if (queryRef.current) {
          lastValidQueryRef.current = new URLSearchParams(queryRef.current.toString());
        }
      })
      .catch(e => {
        if (e instanceof HTTPError && e.response?.data.errors) {
          const errorMsg =
            typeof e.response.data.errors === 'string'
              ? e.response.data.errors
              : JSON.stringify(e.response.data.errors);
          alert(errorMsg);

          if (lastValidQueryRef.current) {
            queryRef.current = new URLSearchParams(lastValidQueryRef.current.toString());

            window.dispatchEvent(
              new CustomEvent('ticketing-instruction-query-reset', {
                detail: {
                  startDateFrom: queryRef.current.get('start_date_from') || null,
                  startDateTo: queryRef.current.get('start_date_to') || null,
                  receivedDatetimeFrom: queryRef.current.get('received_datetime_from') || null,
                  receivedDatetimeTo: queryRef.current.get('received_datetime_to') || null
                }
              })
            );
          }
        } else {
          alert(`エラーが発生しました: ${e.message}`);
        }
      })
      .finally(() => setIsLoading(false));
  }, [queryRef, history, page]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const sendTicketingInstruction = useCallback(
    async (ti: TicketingInstruction) => {
      const params = {
        order_item_id: ti.orderItemId,
        ticketing_method: ti.ticketingMethod,
        ticketing_note: ti.ticketingNote,
        supplied_item_ids: ti.suppliedItemIds.map(id => id?.toString() ?? ''),
        payment_method_ids: ti.paymentMethodIds?.map(id => id?.toString() ?? '')
      };
      await Fetcher.post<Response>('/arrangement/ticketing_instructions.json', params).catch(e => {
        alert(e instanceof HTTPError && e.response?.data.errors ? e.response?.data.errors : e.message);

        throw e;
      });
    },
    [fetchItems]
  );

  return (
    <>
      <TicketingInstructionSearchArea queryRef={queryRef} page={page} onFetch={fetchItems} />
      <Box>未処理件数: {totalCount}件</Box>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>出発日</TableCell>
                <TableCell>依頼日</TableCell>
                <TableCell>企業名</TableCell>
                <TableCell>QR利用企業</TableCell>
                <TableCell>郵送利用企業</TableCell>
                <TableCell>旅程の発券方法</TableCell>
                <TableCell>土日祝対応</TableCell>
                <TableCell>チャットメッセージ</TableCell>
                <TableCell>TripID</TableCell>
                <TableCell>TraceID</TableCell>
                <TableCell>最新のログメッセージ</TableCell>
                <TableCell>内容</TableCell>
                {/* <TableCell>鉄道会社</TableCell> */}
                <TableCell>発券方法</TableCell>
                <TableCell>仕入商品</TableCell>
                <TableCell>備考</TableCell>
                <TableCell>アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticketingInstcutions.map(ti => (
                <TicketingInstructionRow
                  key={ti.orderItemId}
                  ticketingInstruction={ti}
                  suppliers={suppliers}
                  suppliedItems={suppliedItems}
                  paymentMethods={paymentMethods}
                  individualRailwaySuppliedItems={individualRailwaySuppliedItems}
                  optionReady={optionReady}
                  onChange={sendTicketingInstruction}
                />
              ))}
            </TableBody>
          </Table>
          <TablePagination
            count={totalPage}
            page={page}
            onChange={(_: unknown, newPage: number) => setPage(newPage)}
          />
        </>
      )}
    </>
  );
});

export default TicketingInstructionView;
